import React, { useState, useEffect, useRef } from 'react';
import Layout from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { toast } from 'sonner';
import api from '../../utils/api';
import { Plus, Edit, Trash2, Search, Users, Building2, Phone, Mail, Key, Settings, CheckCircle, XCircle, FileSpreadsheet, Download, AlertCircle } from 'lucide-react';
import { WhatsAppChatLink } from '../../components/WhatsAppChatLink';

const TravelAgentManager = () => {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingAgent, setEditingAgent] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  
  // Bulk import state
  const [bulkImportOpen, setBulkImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [validationResult, setValidationResult] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const importFileRef = useRef(null);
  const [formData, setFormData] = useState({
    agent_name: '',
    company_name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    country: 'India',
    gst_number: '',
    password: '',
    is_active: true,
    default_markup_type: 'PERCENT',
    default_markup_value: 10,
    agency_branding_name: '',
  });

  useEffect(() => {
    fetchAgents();
  }, [showInactive]);

  const fetchAgents = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/travel-agents?include_inactive=${showInactive}`);
      setAgents(response.data);
    } catch (error) {
      toast.error('Failed to load travel agents');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const openCreateDialog = () => {
    setEditingAgent(null);
    setFormData({
      agent_name: '',
      company_name: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      state: '',
      country: 'India',
      gst_number: '',
      password: '',
      is_active: true,
      default_markup_type: 'PERCENT',
      default_markup_value: 10,
      agency_branding_name: '',
    });
    setIsDialogOpen(true);
  };

  const openEditDialog = (agent) => {
    setEditingAgent(agent);
    setFormData({
      agent_name: agent.agent_name || '',
      company_name: agent.company_name || '',
      email: agent.email || '',
      phone: agent.phone || '',
      address: agent.address || '',
      city: agent.city || '',
      state: agent.state || '',
      country: agent.country || 'India',
      gst_number: agent.gst_number || '',
      password: '', // Don't show existing password
      is_active: agent.is_active !== false,
      default_markup_type: agent.default_markup_type || 'PERCENT',
      default_markup_value: agent.default_markup_value || 10,
      agency_branding_name: agent.agency_branding_name || '',
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!formData.agent_name) {
      toast.error('Agent name is required');
      return;
    }

    // For new agents or enabling login, require email and password
    if (!editingAgent && formData.password && !formData.email) {
      toast.error('Email is required for login access');
      return;
    }

    try {
      const payload = { ...formData };
      // Don't send empty password
      if (!payload.password) {
        delete payload.password;
      }

      if (editingAgent) {
        await api.put(`/travel-agents/${editingAgent.travel_agent_id}`, payload);
        toast.success('Travel agent updated successfully');
      } else {
        await api.post('/travel-agents', payload);
        toast.success('Travel agent created successfully');
      }
      setIsDialogOpen(false);
      fetchAgents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save travel agent');
    }
  };

  const handleDelete = async (agentId) => {
    if (!window.confirm('Are you sure you want to delete this travel agent?')) return;
    
    try {
      await api.delete(`/travel-agents/${agentId}`);
      toast.success('Travel agent deleted successfully');
      fetchAgents();
    } catch (error) {
      toast.error('Failed to delete travel agent');
    }
  };

  const toggleAgentStatus = async (agent) => {
    try {
      await api.patch(`/travel-agents/${agent.travel_agent_id}/settings`, {
        is_active: !agent.is_active
      });
      toast.success(`Agent ${agent.is_active ? 'deactivated' : 'activated'}`);
      fetchAgents();
    } catch (error) {
      toast.error('Failed to update agent status');
    }
  };

  const filteredAgents = agents.filter(agent =>
    agent.agent_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    agent.company_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    agent.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const activeAgents = agents.filter(a => a.is_active !== false);
  const agentsWithLogin = agents.filter(a => a.email && a.password_hash);

  return (
    <Layout>
      <div className="space-y-6" data-testid="travel-agent-manager">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">B2B Travel Agents</h1>
            <p className="text-gray-600 mt-1">Manage your B2B travel agent partners with login access</p>
          </div>
          <div className="flex items-center gap-2">
            <Dialog open={bulkImportOpen} onOpenChange={(open) => { setBulkImportOpen(open); if (!open) { setImportFile(null); setValidationResult(null); setImportResult(null); if(importFileRef.current) importFileRef.current.value = ''; } }}>
              <Button variant="outline" onClick={() => setBulkImportOpen(true)} data-testid="bulk-import-agents-btn">
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                Import
              </Button>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Bulk Import B2B Agents</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium text-sm">Download Template</p>
                      <p className="text-xs text-gray-500">CSV format with required columns</p>
                    </div>
                    <Button variant="outline" size="sm" onClick={async () => {
                      try {
                        const response = await api.get('/admin/bulk-import/travel-agents/template', { responseType: 'blob' });
                        const url = window.URL.createObjectURL(new Blob([response.data]));
                      const link = document.createElement('a');
                      link.href = url;
                      link.setAttribute('download', 'b2b_agents_template.csv');
                      document.body.appendChild(link);
                      link.click();
                      link.remove();
                    } catch (error) {
                      toast.error('Failed to download template');
                    }
                  }}>
                    <Download className="w-4 h-4 mr-2" />
                    Template
                  </Button>
                </div>
                
                <div className="space-y-2">
                  <Label>Upload File (CSV or Excel)</Label>
                  <Input
                    ref={importFileRef}
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={async (e) => {
                      const file = e.target.files[0];
                      if (!file) return;
                      setImportFile(file);
                      setValidationResult(null);
                      setImportResult(null);
                      const formData = new FormData();
                      formData.append('file', file);
                      try {
                        const response = await api.post('/admin/bulk-import/travel-agents/validate', formData, {
                          headers: { 'Content-Type': 'multipart/form-data' }
                        });
                        setValidationResult(response.data);
                      } catch (error) {
                        toast.error('Failed to validate file');
                        setValidationResult({ valid: false, errors: [error.response?.data?.detail || 'Validation failed'] });
                      }
                    }}
                  />
                </div>
                
                {validationResult && (
                  <div className={`p-3 rounded-lg ${validationResult.valid ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                    <div className="flex items-center gap-2 mb-2">
                      {validationResult.valid ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : (
                        <AlertCircle className="w-5 h-5 text-red-600" />
                      )}
                      <span className={`font-medium ${validationResult.valid ? 'text-green-700' : 'text-red-700'}`}>
                        {validationResult.valid ? 'Validation Passed' : 'Validation Failed'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">
                      {validationResult.valid_rows} of {validationResult.total_rows} rows valid
                    </p>
                    {validationResult.errors?.length > 0 && (
                      <ul className="mt-2 text-xs text-red-600 list-disc list-inside max-h-32 overflow-y-auto">
                        {validationResult.errors.map((err, i) => <li key={i}>{err}</li>)}
                      </ul>
                    )}
                  </div>
                )}
                
                {importResult && (
                  <div className={`p-3 rounded-lg ${importResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                    <p className={`font-medium ${importResult.success ? 'text-green-700' : 'text-red-700'}`}>
                      {importResult.success ? `Created: ${importResult.created} agents` : 'Import Failed'}
                    </p>
                    {importResult.errors?.length > 0 && (
                      <ul className="mt-2 text-xs text-red-600 list-disc list-inside">
                        {importResult.errors.slice(0, 5).map((err, i) => <li key={i}>{err}</li>)}
                      </ul>
                    )}
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setBulkImportOpen(false)}>Close</Button>
                <Button 
                  onClick={async () => {
                    if (!importFile || !validationResult?.valid) return;
                    setImporting(true);
                    const formData = new FormData();
                    formData.append('file', importFile);
                    try {
                      const response = await api.post('/admin/bulk-import/travel-agents', formData, {
                        headers: { 'Content-Type': 'multipart/form-data' }
                      });
                      setImportResult(response.data);
                      if (response.data.success) {
                        toast.success(`Imported ${response.data.created} agents`);
                        fetchAgents();
                      }
                    } catch (error) {
                      toast.error('Import failed');
                      setImportResult({ success: false, errors: [error.response?.data?.detail || 'Import failed'] });
                    } finally {
                      setImporting(false);
                    }
                  }}
                  disabled={!validationResult?.valid || importing}
                >
                  {importing ? 'Importing...' : 'Import'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <Button onClick={openCreateDialog} data-testid="add-agent-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Travel Agent
          </Button>
          </div>
        </div>

        {/* Search & Filter */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Search by name, company or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={showInactive}
                  onCheckedChange={setShowInactive}
                  id="show-inactive"
                />
                <Label htmlFor="show-inactive" className="text-sm">Show Inactive</Label>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{agents.length}</p>
                  <p className="text-sm text-gray-600">Total Agents</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-green-100 rounded-lg">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{activeAgents.length}</p>
                  <p className="text-sm text-gray-600">Active</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-purple-100 rounded-lg">
                  <Key className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{agentsWithLogin.length}</p>
                  <p className="text-sm text-gray-600">With Login</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <Building2 className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">
                    {agents.filter(a => a.gst_number).length}
                  </p>
                  <p className="text-sm text-gray-600">GST Registered</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Agent List */}
        <Card>
          <CardHeader>
            <CardTitle>Travel Agent List</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">Loading...</div>
            ) : filteredAgents.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                {searchQuery ? 'No agents match your search' : 'No travel agents found. Add your first agent!'}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Agent Name</TableHead>
                      <TableHead>Company</TableHead>
                      <TableHead>Contact</TableHead>
                      <TableHead>Portal Access</TableHead>
                      <TableHead>Markup</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="w-[100px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredAgents.map((agent) => (
                      <TableRow 
                        key={agent.travel_agent_id} 
                        data-testid={`agent-row-${agent.travel_agent_id}`}
                        className={agent.is_active === false ? 'opacity-50' : ''}
                      >
                        <TableCell className="font-medium">{agent.agent_name}</TableCell>
                        <TableCell>{agent.company_name || '-'}</TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            {agent.phone && (
                              <div className="flex items-center gap-1 text-sm">
                                <Phone className="w-3 h-3" />
                                {agent.phone}
                                <WhatsAppChatLink phone={agent.phone} testId={`agent-whatsapp-${agent.agent_id || agent.travel_agent_id || agent.id}`} />
                              </div>
                            )}
                            {agent.email && (
                              <div className="flex items-center gap-1 text-sm text-gray-600">
                                <Mail className="w-3 h-3" />
                                {agent.email}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {agent.email ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                              <Key className="w-3 h-3" />
                              Enabled
                            </span>
                          ) : (
                            <span className="text-gray-400 text-sm">No login</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <span className="text-sm">
                            {agent.default_markup_type === 'PERCENT' 
                              ? `${agent.default_markup_value}%`
                              : `₹${agent.default_markup_value}`
                            }
                          </span>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => toggleAgentStatus(agent)}
                            className={agent.is_active !== false ? 'text-green-600' : 'text-gray-400'}
                          >
                            {agent.is_active !== false ? (
                              <CheckCircle className="w-4 h-4" />
                            ) : (
                              <XCircle className="w-4 h-4" />
                            )}
                          </Button>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditDialog(agent)}
                              data-testid={`edit-agent-${agent.travel_agent_id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-600 hover:text-red-700"
                              onClick={() => handleDelete(agent.travel_agent_id)}
                              data-testid={`delete-agent-${agent.travel_agent_id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Add/Edit Dialog */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingAgent ? 'Edit Travel Agent' : 'Add Travel Agent'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-6 py-4">
              {/* Basic Info */}
              <div className="space-y-4">
                <h3 className="font-medium text-sm text-gray-500 border-b pb-2">Basic Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="agent_name">
                      Contact Person <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="agent_name"
                      value={formData.agent_name}
                      onChange={(e) => handleInputChange('agent_name', e.target.value)}
                      placeholder="Contact person name"
                      data-testid="agent-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="company_name">Company Name</Label>
                    <Input
                      id="company_name"
                      value={formData.company_name}
                      onChange={(e) => handleInputChange('company_name', e.target.value)}
                      placeholder="Travel agency name"
                      data-testid="company-name-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone / WhatsApp</Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      placeholder="10 digit number"
                      data-testid="phone-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="gst_number">GST Number</Label>
                    <Input
                      id="gst_number"
                      value={formData.gst_number}
                      onChange={(e) => handleInputChange('gst_number', e.target.value.toUpperCase())}
                      placeholder="22AAAAA0000A1Z5"
                      data-testid="gst-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="address">Address</Label>
                  <Textarea
                    id="address"
                    value={formData.address}
                    onChange={(e) => handleInputChange('address', e.target.value)}
                    placeholder="Full address"
                    rows={2}
                    data-testid="address-input"
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="city">City</Label>
                    <Input
                      id="city"
                      value={formData.city}
                      onChange={(e) => handleInputChange('city', e.target.value)}
                      placeholder="City"
                      data-testid="city-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="state">State</Label>
                    <Input
                      id="state"
                      value={formData.state}
                      onChange={(e) => handleInputChange('state', e.target.value)}
                      placeholder="State"
                      data-testid="state-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="country">Country</Label>
                    <Input
                      id="country"
                      value={formData.country}
                      onChange={(e) => handleInputChange('country', e.target.value)}
                      placeholder="Country"
                      data-testid="country-input"
                    />
                  </div>
                </div>
              </div>

              {/* Portal Login */}
              <div className="space-y-4">
                <h3 className="font-medium text-sm text-gray-500 border-b pb-2 flex items-center gap-2">
                  <Key className="w-4 h-4" />
                  Partner Portal Login
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Login Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      placeholder="agent@company.com"
                      data-testid="email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password">
                      {editingAgent ? 'New Password (leave blank to keep)' : 'Password'}
                    </Label>
                    <Input
                      id="password"
                      type="password"
                      value={formData.password}
                      onChange={(e) => handleInputChange('password', e.target.value)}
                      placeholder={editingAgent ? '••••••••' : 'Set password'}
                      data-testid="password-input"
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-500">
                  Set email and password to enable login at <code className="bg-gray-100 px-1 rounded">/login</code> (same screen as everyone else)
                </p>
              </div>

              {/* Settings */}
              <div className="space-y-4">
                <h3 className="font-medium text-sm text-gray-500 border-b pb-2 flex items-center gap-2">
                  <Settings className="w-4 h-4" />
                  Settings
                </h3>
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Account Status</Label>
                    <p className="text-xs text-gray-500">Enable or disable this agent's access</p>
                  </div>
                  <Switch
                    checked={formData.is_active}
                    onCheckedChange={(checked) => handleInputChange('is_active', checked)}
                    data-testid="status-switch"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Default Markup Type</Label>
                    <Select 
                      value={formData.default_markup_type} 
                      onValueChange={(v) => handleInputChange('default_markup_type', v)}
                    >
                      <SelectTrigger data-testid="markup-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="PERCENT">Percentage (%)</SelectItem>
                        <SelectItem value="VALUE">Fixed Value (₹)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Default Markup Value</Label>
                    <Input
                      type="number"
                      value={formData.default_markup_value}
                      onChange={(e) => handleInputChange('default_markup_value', parseFloat(e.target.value) || 0)}
                      placeholder={formData.default_markup_type === 'PERCENT' ? '10' : '1000'}
                      data-testid="markup-value-input"
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-500">
                  This markup is automatically added to all quotations created by this agent
                </p>
              </div>

              {/* Branding */}
              <div className="space-y-4">
                <h3 className="font-medium text-sm text-gray-500 border-b pb-2 flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  Branding
                </h3>
                <div className="space-y-2">
                  <Label htmlFor="agency_branding_name">Display Name on Quotations</Label>
                  <Input
                    id="agency_branding_name"
                    value={formData.agency_branding_name}
                    onChange={(e) => handleInputChange('agency_branding_name', e.target.value)}
                    placeholder={formData.company_name || "Company name to show on quotes"}
                    data-testid="branding-name-input"
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSubmit} data-testid="save-agent-btn">
                {editingAgent ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default TravelAgentManager;
